import React, { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'

import useTranslate from '../../hooks/useTranslate'
import { transformCTAWithBackground } from 'marketing-site/src/transformers/elements/ContentfulCTALink'
import { Background } from '../../elements/Background'
import { CloseButton } from 'marketing-site/src/mvp/library/elements/CloseButton'
import { CTALink } from '../../elements/CTALink/element'
import { Image } from '../../elements/Image/index'
import { Text } from '../../elements/Text'
import { VisuallyHidden } from '../../elements/VisuallyHidden'
import { Icon, renderIconSVG } from '../../utils'
import { IProps } from './index'

export const ExitIntentPopupV2 = ({
  isOpen,
  onClose,
  theme,
  image,
  headline,
  checkmarks,
  cta,
  onCtaClicked,
}: IProps): React.ReactElement => {
  const popupRef = useRef<HTMLDivElement>(null)
  const [popupWidth, setPopupWidth] = useState(0)
  const [popupOpenBottomMargin, setPopupOpenBottomMargin] = useState(0)
  const [intercomBannerVariant, setIntercomBannerVariant] = useState<
    'inline-top' | 'inline-bottom' | 'floating-top' | 'floating-bottom' | null
  >(null)

  const { translate } = useTranslate()

  useEffect(() => {
    if (popupRef.current) {
      const resizeObserver = new ResizeObserver(([entry]) => setPopupWidth(entry.contentRect.width))
      resizeObserver.observe(popupRef.current)

      const mutationObserver = new MutationObserver((mutations, observer) => {
        for (const mutation of mutations) {
          mutation.addedNodes.forEach((addedNode) => {
            if ((addedNode as HTMLIFrameElement).name === 'intercom-banner-frame') {
              if (window.location.href.includes('roofline-qa-inline-bottom')) {
                setIntercomBannerVariant('inline-bottom')
              } else if (window.location.href.includes('roofline-qa-floating-bottom')) {
                setIntercomBannerVariant('floating-bottom')
              } else if (window.location.href.includes('roofline-qa-inline')) {
                setIntercomBannerVariant('inline-top')
              } else if (window.location.href.includes('roofline-qa-floating')) {
                setIntercomBannerVariant('floating-top')
              }
            }
          })
          mutation.removedNodes.forEach((removedNode) => {
            if ((removedNode as HTMLIFrameElement).name === 'intercom-banner-frame') {
              setIntercomBannerVariant(null)
              observer.disconnect()
            }
          })
        }
      })
      mutationObserver.observe(document.body, { childList: true, subtree: true })
    }
  }, [])

  useEffect(() => {
    const intercomBannerBox = document.querySelector<HTMLIFrameElement>(
      'iframe[name="intercom-banner-frame"]',
    )
    const cookiesBannerBox = document.querySelector<HTMLDivElement>(
      '#intercom-cookie-consent-banner-wrap > div',
    )
    let intercomBannerBoxHeight = 0
    if (document.body.clientWidth < 355) {
      intercomBannerBoxHeight = 122
    } else if (document.body.clientWidth < 600) {
      intercomBannerBoxHeight = 102
    } else if (document.body.clientWidth < 694) {
      intercomBannerBoxHeight = 82
    } else {
      intercomBannerBoxHeight = 50
    }

    switch (intercomBannerVariant) {
      case 'inline-bottom':
        setPopupOpenBottomMargin(intercomBannerBoxHeight)
        break
      case 'floating-bottom':
        if (intercomBannerBox && !cookiesBannerBox) {
          intercomBannerBox.style.setProperty(
            'bottom',
            popupRef.current?.classList.contains('open')
              ? `${(popupRef.current?.offsetHeight || 0) + 16}px`
              : '16px',
          )
        }
        break
      case null:
      default:
        setPopupOpenBottomMargin(0)
        break
    }
  }, [popupWidth, intercomBannerVariant, isOpen])

  const popupClassNames = classNames('exit-intent-popup', {
    open: isOpen,
  })

  return (
    <>
      <section className={popupClassNames} ref={popupRef}>
        <Background theme={theme}>
          <div className="exit-intent-popup__close">
            <CloseButton onClick={onClose} />
            <VisuallyHidden>{translate('Close')}</VisuallyHidden>
          </div>
          <div className={'exit-intent-popup__content'}>
            <figure className="exit-intent-popup__image">
              <Image url={image.url} altText={image.altText} />
            </figure>
            <article className="exit-intent-popup__text">
              <header className="exit-intent-popup__header">
                <Text brand="default" size="heading-3">
                  {headline}
                </Text>
              </header>
              <div className="exit-intent-popup__text__points">
                <ul>
                  {checkmarks.map((text) => (
                    <li key={text}>
                      {renderIconSVG(Icon.Checkmark)}
                      {text}
                    </li>
                  ))}
                </ul>
              </div>
              <footer className="exit-intent-popup__footer">
                <CTALink {...transformCTAWithBackground(cta)} onClick={onCtaClicked} wide={true} />
              </footer>
            </article>
          </div>
        </Background>
      </section>

      <style jsx>{`
        .exit-intent-popup {
          width: 100%;
          position: fixed;
          top: 100%;
          transition: transform 0.5s cubic-bezier(0.93, 0.07, 0.27, 0.97);
          box-shadow: 0 2px 3px rgba(0, 0, 0, 0.2), 0 2px 15px rgba(0, 0, 0, 0.3);
          z-index: 5;

          :global(> *) {
            padding: 40px 0 0;
          }

          &.open {
            transform: translateY(calc(-100% - ${popupOpenBottomMargin}px));
            transition: transform 0.5s cubic-bezier(0.11, 1.03, 0.21, 0.97);
          }

          &__content {
            display: grid;
            grid-template-columns: 2fr [image] 6fr [content] 6fr 2fr;
            align-items: center;
            max-width: $container-max-width;
            margin: 0 auto;
          }

          &__image {
            grid-column: image;
            box-shadow: 0px 4.68965px 4.68965px rgba(0, 0, 0, 0.25);
            border-radius: 10px 10px 0 0;
            position: relative;
            overflow: hidden;
            align-self: flex-end;
          }
          &__text {
            grid-column: content;
            padding-left: 48px;
            padding-bottom: 40px;

            &__points {
              li {
                margin: 8px 0;

                :global(svg) {
                  margin-right: 16px;
                  position: relative;
                  top: 2px;
                }
              }
            }
          }

          &__header {
            margin-bottom: 16px;
          }

          &__footer {
            margin-top: 32px;
            display: block;
          }

          &__close {
            @include focusable;
            position: absolute;
            top: 1.5rem;
            right: 1.5rem;

            &:focus {
              position: absolute;
            }
          }
        }
      `}</style>
    </>
  )
}
